import { Link } from 'react-router-dom';

const Section = ({ title, children }) => (
    <section className="mb-8">
        <h2 className="text-lg md:text-xl font-semibold text-[#1B4D3E] mb-3">{title}</h2>
        <div className="space-y-3 text-slate-600 text-sm md:text-base leading-relaxed">{children}</div>
    </section>
);

export default function TermsOfServicePage() {
    return (
        <div className="bg-slate-50 min-h-screen py-12" data-testid="terms-of-service-page">
            <div className="container-main max-w-3xl">
                <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6 md:p-10">
                    <h1 className="text-3xl md:text-4xl font-bold text-slate-900 mb-2">Terms of Service</h1>
                    <p className="text-sm text-slate-400 mb-8">Last updated: June 2026</p>

                    <Section title="1. Agreement to Terms">
                        <p>
                            These Terms of Service ("Terms") govern your access to and use of the website and
                            wholesale ordering platform operated by <strong>Henry's Aussie Fruit &amp; Veggies</strong>
                            ("we", "us", "our") at{' '}
                            <a href="https://www.henrysaussiefv.com.au" className="text-[#1B4D3E] underline">
                                www.henrysaussiefv.com.au
                            </a>{' '}
                            (the "Service"). By accessing or using the Service, you agree to be bound by these Terms.
                            If you do not agree, please do not use the Service.
                        </p>
                    </Section>

                    <Section title="2. Eligibility">
                        <p>
                            The Service is intended for businesses and individuals aged 18 or over placing wholesale
                            produce orders. By using the Service you represent that you meet these requirements.
                        </p>
                    </Section>

                    <Section title="3. Accounts">
                        <ul className="list-disc pl-6 space-y-1">
                            <li>You may create an account using your email address or by signing in with Google.</li>
                            <li>You are responsible for keeping your login credentials confidential and for all activity under your account.</li>
                            <li>You agree to provide accurate, current, and complete information and to keep it up to date.</li>
                            <li>Notify us promptly of any unauthorised use of your account.</li>
                        </ul>
                    </Section>

                    <Section title="4. Orders &amp; Pricing">
                        <ul className="list-disc pl-6 space-y-1">
                            <li>Prices are shown per kilogram or per unit and are subject to change based on market conditions.</li>
                            <li>Many items are sold by weight. The amounts shown at checkout are estimates; the <strong>final price is calculated from the actual weighed quantity</strong> once your order is prepared.</li>
                            <li>Orders are subject to availability and acceptance by us.</li>
                            <li>Order cut-off and delivery/pickup timing are shown on the site and may change from time to time.</li>
                        </ul>
                    </Section>

                    <Section title="5. Payment">
                        <p>
                            Once your order is weighed and marked ready, we generate a secure payment link (processed
                            by Stripe) for the final total, which is sent to your email. Payment is due upon receipt of
                            the link. We do not store your full card details. All amounts are in Australian Dollars (AUD).
                        </p>
                    </Section>

                    <Section title="6. Delivery &amp; Pickup — Cut-off Times &amp; Schedule">
                        <p>
                            We offer both delivery and pickup, each on a fixed weekly schedule (all times Australia/Sydney):
                        </p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>
                                <span className="font-semibold">Delivery:</span> order cut-off is <span className="font-semibold">Saturday 12:00 PM</span>.
                                Orders placed before this cut-off are delivered on the <span className="font-semibold">Tuesday of the following week</span>.
                            </li>
                            <li>
                                <span className="font-semibold">Pickup:</span> order cut-off is <span className="font-semibold">Thursday 12:00 PM</span>.
                                Orders placed before this cut-off are ready for pickup on the <span className="font-semibold">Saturday of the same week, 7:00 AM – 1:00 PM</span>,
                                at <span className="font-semibold">Orange Grove Market, Lilyfield NSW 2040</span>.
                            </li>
                        </ul>
                        <p>
                            You are responsible for providing accurate delivery details. Risk in the goods passes to you upon
                            delivery or pickup. Delivery windows are estimates and may vary due to circumstances beyond our control.
                        </p>
                    </Section>

                    <Section title="7. Cancellations &amp; Refunds">
                        <p>
                            You may cancel your own order online only while it is still <span className="font-semibold">"Pending"</span> (i.e. we have not yet
                            confirmed it), <span className="font-semibold">within 24 hours of placing it</span>, and <span className="font-semibold">before the
                            applicable order cut-off</span> (Saturday 12:00 PM for delivery, Thursday 12:00 PM for pickup) — whichever comes first.
                            Once an order has been confirmed, or any of those limits has passed, it can no longer be cancelled online and you
                            will need to contact us. Once an order is paid and/or dispatched, cancellations and refunds are handled on a
                            case-by-case basis. If a refund is due, it will be processed via the original payment method. Please contact us as
                            soon as possible with any issues regarding your order or its quality.
                        </p>
                    </Section>

                    <Section title="8. Acceptable Use">
                        <p>You agree not to misuse the Service, including by:</p>
                        <ul className="list-disc pl-6 space-y-1">
                            <li>attempting to gain unauthorised access to any part of the Service or another user's account;</li>
                            <li>interfering with or disrupting the Service or its security features; or</li>
                            <li>using the Service for any unlawful or fraudulent purpose.</li>
                        </ul>
                    </Section>

                    <Section title="9. Intellectual Property">
                        <p>
                            All content on the Service, including logos, text, and images, is owned by or licensed to
                            Henry's Aussie Fruit &amp; Veggies and is protected by applicable laws. You may not use it
                            without our prior written permission.
                        </p>
                    </Section>

                    <Section title="10. Disclaimers &amp; Limitation of Liability">
                        <p>
                            The Service is provided on an "as is" and "as available" basis. To the maximum extent
                            permitted by law, we exclude all warranties not expressly stated and are not liable for any
                            indirect or consequential loss arising from your use of the Service. Nothing in these Terms
                            limits any rights you have under the Australian Consumer Law.
                        </p>
                    </Section>

                    <Section title="11. Governing Law">
                        <p>
                            These Terms are governed by the laws of New South Wales, Australia, and you submit to the
                            non-exclusive jurisdiction of the courts of that state.
                        </p>
                    </Section>

                    <Section title="12. Changes to These Terms">
                        <p>
                            We may update these Terms from time to time. Continued use of the Service after changes are
                            posted constitutes acceptance of the updated Terms.
                        </p>
                    </Section>

                    <Section title="13. Contact Us">
                        <p>Questions about these Terms? Contact us:</p>
                        <ul className="list-none space-y-1">
                            <li><strong>Henry's Aussie Fruit &amp; Veggies</strong></li>
                            <li>Sydney, Australia</li>
                            <li>Email: <a href="mailto:henrys3@bigpond.com" className="text-[#1B4D3E] underline">henrys3@bigpond.com</a></li>
                            <li>Phone: <a href="tel:+61414829929" className="text-[#1B4D3E] underline">+61 414 829 929</a></li>
                        </ul>
                    </Section>

                    <div className="pt-4 border-t border-slate-100">
                        <Link to="/" className="text-sm text-[#1B4D3E] underline" data-testid="terms-home-link">← Back to Home</Link>
                    </div>
                </div>
            </div>
        </div>
    );
}
